import React, { useCallback, useEffect } from 'react';
import { usePusher } from '@/App';
import { MyAppointments as MyAppointmentsAction } from '@/actions';
import LoadingV2 from '@/components/Loading';
import { TableV3 } from '@/components/TableV3';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { formatDateTimeString } from '@/screens/Report/utils/dateHelpers';

const ReportYourAppointments = ({
  columns,
  containerName,
  filters,
  filtersLoaded,
  orderBy,
  setOrderBy,
  handleExpandRow,
  handleResponse,
  relatedAppointments,
  loadingRelated,
  expandedRow,
  selectedRowForLinks,
  dateRangePicker,
  selectedDayIndex,
  chartLabels,
}) => {
  const { channels } = usePusher();

  // Extract date range dependencies for useInfiniteScroll
  const startDate = dateRangePicker?.startDate;
  const endDate = dateRangePicker?.endDate;

  const fetchApi = useCallback(
    (page) => {
      if (!filtersLoaded) {
        return Promise.resolve({ data: { appointments: { data: [] } } });
      }

      // Build API params - same as YourAppointments
      const apiParams = {
        statuses: filters.statuses.map(({ value }) => value),
        types: filters.types.map(({ value }) => value),
        terminals: filters.terminals.map(({ value }) => value),
        shipping_lines: filters.shippingLines.map(({ value }) => value),
        container_name: containerName,
        origin_sources: filters.originSource.map(({ value }) => value),
        autobook_statuses: filters.autobookStatus.map(({ value }) => value),
        page: page,
        per_page: 15,
        order_by: orderBy,
        dateFrom: startDate ? formatDateTimeString(startDate) : null,
        dateTill: endDate ? formatDateTimeString(endDate) : null,
      };

      return MyAppointmentsAction(apiParams);
    },
    [
      filters.statuses,
      filters.types,
      filters.terminals,
      filters.shippingLines,
      containerName,
      filters.originSource,
      filters.autobookStatus,
      orderBy,
      filtersLoaded,
      startDate,
      endDate,
    ]
  );

  // Always provide the same number of dependencies to avoid React warnings
  // Use empty arrays/values when filters aren't loaded yet
  const depsArray = filtersLoaded
    ? [
        filters.statuses,
        filters.types,
        filters.terminals,
        filters.shippingLines,
        containerName,
        filters.originSource,
        filters.autobookStatus,
        orderBy,
        selectedDayIndex,
        startDate,
        endDate,
      ]
    : [
        [], // filters.statuses
        [], // filters.types
        [], // filters.terminals
        [], // filters.shippingLines
        null, // containerName
        [], // filters.originSource
        [], // filters.autobookStatus
        null, // orderBy
        null, // selectedDayIndex
        null, // startDate
        null, // endDate
      ];

  const {
    items: DATATable,
    loading: loadingData,
    refetch,
  } = useInfiniteScroll(fetchApi, {
    deps: depsArray,
    searchText: containerName,
    responseDataKey: 'appointments',
    handleResponse,
  });

  const isNearTopOrFirstPage = useCallback(() => {
    const mainEl = document.getElementsByTagName('main')[0];
    const scrollTop = mainEl ? mainEl.scrollTop : window.scrollY || 0;
    const nearTop = scrollTop <= 50; // threshold

    return nearTop;
  }, []);

  useEffect(() => {
    if (channels?.privateChannel) {
      channels.privateChannel.bind('ContainerAppointmentUpdate2', () => {
        // Auto-refresh data only if the user is near the top/first page.
        if (isNearTopOrFirstPage()) {
          refetch();
        }
      });

      return () => {
        channels.privateChannel.unbind('ContainerAppointmentUpdate2');
      };
    }
  }, [channels, fetchApi, isNearTopOrFirstPage, refetch]);

  const handleOrderBy = (orderBy) => {
    setOrderBy(orderBy);
  };

  return (
    <>
      {!(loadingData && DATATable.length === 0) && (
        <div className="mb-4">
          <TableV3
            isExpandable
            headers={columns}
            data={DATATable || []}
            emptyMessage=""
            minWidth="1510px"
            stickyHeader={true}
            appHeaderHeight={64}
            onSort={handleOrderBy}
            currentSortKey={orderBy}
            expandedRow={expandedRow}
            relatedAppointments={relatedAppointments}
            loadingRelated={loadingRelated}
            onExpandRow={handleExpandRow}
            localStorageKey="report-appointments-selected-row"
            getRowClassName={(item) =>
              selectedRowForLinks?.id === item.id ? 'z-99' : 'z-9'
            }
            selectedRowForLinks={selectedRowForLinks}
          />
        </div>
      )}

      {loadingData &&
        (DATATable.length === 0 || (DATATable && DATATable.length > 0)) && (
          <div className="flex justify-center py-4">
            <LoadingV2 />
          </div>
        )}
    </>
  );
};

export default ReportYourAppointments;
